(() => {
  const ROOT_SELECTOR = 'cardology-header.cdo-header';
  const LOCK_CLASS = 'cdo-header-lock';
  const controllers = new WeakMap();

  class HeaderController {
    constructor(root) {
      this.root = root;
      this.closeTimer = null;
      this.lastFocusedElement = null;
      this.cartCountSyncTimer = null;
      this.cartCountSyncRequest = 0;
      this.cartUpdateUnsubscriber = null;
      this.cartObserver = null;
      this.megaTriggers = Array.from(root.querySelectorAll('[data-cdo-mega-trigger]'));
      this.megaPanels = Array.from(root.querySelectorAll('[data-cdo-mega-panel]'));
      this.mobileDrawer = root.querySelector('[data-cdo-mobile-drawer]');
      this.searchOverlay = root.querySelector('[data-cdo-search-overlay]');

      this.onRootClick = this.onRootClick.bind(this);
      this.onDocumentKeydown = this.onDocumentKeydown.bind(this);
      this.onDocumentPointerDown = this.onDocumentPointerDown.bind(this);
      this.onResize = this.onResize.bind(this);
      this.onRuntimeCartUpdate = this.onRuntimeCartUpdate.bind(this);

      this.bind();
    }

    bind() {
      this.root.dataset.cdoHeaderReady = 'true';
      this.root.addEventListener('click', this.onRootClick);

      this.megaTriggers.forEach((trigger) => {
        trigger.addEventListener('mouseenter', () => this.openMega(trigger.dataset.cdoMegaTrigger, trigger));
        trigger.addEventListener('mouseleave', () => this.scheduleMegaClose());
        trigger.addEventListener('focus', () => this.openMega(trigger.dataset.cdoMegaTrigger, trigger));
      });

      this.megaPanels.forEach((panel) => {
        panel.addEventListener('mouseenter', () => this.cancelMegaClose());
        panel.addEventListener('mouseleave', () => this.scheduleMegaClose());
        panel.addEventListener('focusin', () => this.cancelMegaClose());
        panel.addEventListener('focusout', (event) => {
          if (!panel.contains(event.relatedTarget)) this.scheduleMegaClose();
        });
      });

      if (this.searchOverlay) {
        this.searchOverlay.addEventListener('pointerdown', (event) => {
          if (event.target === this.searchOverlay) this.closeSearch();
        });
      }

      document.addEventListener('keydown', this.onDocumentKeydown);
      document.addEventListener('pointerdown', this.onDocumentPointerDown);
      document.addEventListener('cart:updated', this.onRuntimeCartUpdate);
      document.addEventListener('cart:refresh', this.onRuntimeCartUpdate);
      window.addEventListener('oe:cart:updated', this.onRuntimeCartUpdate);
      window.addEventListener('resize', this.onResize, { passive: true });

      if (
        typeof subscribe === 'function' &&
        typeof PUB_SUB_EVENTS !== 'undefined' &&
        PUB_SUB_EVENTS.cartUpdate
      ) {
        this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.onRuntimeCartUpdate);
      }

      this.observeCartMutations();
    }

    destroy() {
      this.cancelMegaClose();
      if (this.cartCountSyncTimer !== null) {
        window.clearTimeout(this.cartCountSyncTimer);
        this.cartCountSyncTimer = null;
      }
      if (typeof this.cartUpdateUnsubscriber === 'function') {
        this.cartUpdateUnsubscriber();
        this.cartUpdateUnsubscriber = null;
      }
      if (this.cartObserver) {
        this.cartObserver.disconnect();
        this.cartObserver = null;
      }
      this.root.removeEventListener('click', this.onRootClick);
      document.removeEventListener('keydown', this.onDocumentKeydown);
      document.removeEventListener('pointerdown', this.onDocumentPointerDown);
      document.removeEventListener('cart:updated', this.onRuntimeCartUpdate);
      document.removeEventListener('cart:refresh', this.onRuntimeCartUpdate);
      window.removeEventListener('oe:cart:updated', this.onRuntimeCartUpdate);
      window.removeEventListener('resize', this.onResize);
      delete this.root.dataset.cdoHeaderReady;
    }

    observeCartMutations() {
      if (typeof MutationObserver === 'undefined') return;

      const cartSurface =
        document.querySelector('cart-drawer') ||
        document.querySelector('cart-notification') ||
        document.querySelector('#main-cart');

      if (!cartSurface) return;

      this.cartObserver = new MutationObserver((mutations) => {
        const changed = mutations.some((mutation) => {
          if (mutation.type === 'childList') return true;
          return mutation.type === 'attributes' && mutation.attributeName === 'value';
        });

        if (changed) this.onRuntimeCartUpdate();
      });

      this.cartObserver.observe(cartSurface, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['value'],
      });
    }

    onRuntimeCartUpdate(event) {
      if (event?.detail?.source === 'collection-aov-view-basket') {
        this.openRuntimeCartDrawer();
      }

      if (event?.type === 'oe:cart:updated' && event.detail?.cart) {
        this.applyCartCount(event.detail.cart);
        return;
      }

      if (this.cartCountSyncTimer !== null) {
        window.clearTimeout(this.cartCountSyncTimer);
      }

      this.cartCountSyncTimer = window.setTimeout(() => {
        this.cartCountSyncTimer = null;
        this.syncCartCount();
      }, 80);
    }

    async openRuntimeCartDrawer() {
      const cartDrawer = document.querySelector('cart-drawer');
      const cartTrigger = document.getElementById('cart-icon-bubble');

      if (!cartDrawer) {
        window.location.assign(window.routes?.cart_url || '/cart');
        return;
      }

      try {
        const sectionIds = typeof cartDrawer.getSectionsToRender === 'function'
          ? cartDrawer.getSectionsToRender().map((section) => section.id).filter(Boolean)
          : ['cart-drawer', 'cart-icon-bubble'];

        const sectionUrl = new URL(window.location.href);
        sectionUrl.search = '';
        sectionUrl.searchParams.set('sections', sectionIds.join(','));

        const response = await fetch(sectionUrl.toString(), {
          method: 'GET',
          headers: {
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          credentials: 'same-origin',
          cache: 'no-store',
        });

        if (!response.ok) throw new Error(`Cart sections request failed: ${response.status}`);
        const sections = await response.json();

        if (typeof cartDrawer.setActiveElement === 'function' && cartTrigger) {
          cartDrawer.setActiveElement(cartTrigger);
        }

        if (typeof cartDrawer.renderContents === 'function') {
          cartDrawer.renderContents({ sections });
          return;
        }
      } catch (error) {
        console.warn('Cardology header: unable to refresh cart drawer before opening.', error);
      }

      if (typeof cartDrawer.open === 'function') {
        cartDrawer.open(cartTrigger || undefined);
      } else if (cartTrigger) {
        cartTrigger.click();
      } else {
        window.location.assign(window.routes?.cart_url || '/cart');
      }
    }

    applyCartCount(cart) {
      const fallbackCount = Array.isArray(cart?.items)
        ? cart.items.reduce((total, item) => total + Math.max(0, Number(item?.quantity || 0)), 0)
        : 0;
      const count = Number(cart?.item_count ?? fallbackCount);

      this.root.querySelectorAll('.cdo-header__cart-count').forEach((element) => {
        element.textContent = String(count);
      });

      const cartLink = this.root.querySelector('#cart-icon-bubble');
      if (cartLink) {
        cartLink.setAttribute('aria-label', count === 1 ? 'Cart, 1 item' : `Cart, ${count} items`);
      }
    }

    async syncCartCount() {
      const requestId = ++this.cartCountSyncRequest;

      try {
        const rootPath = window.Shopify?.routes?.root || '/';
        const response = await fetch(`${rootPath}cart.js`, {
          method: 'GET',
          headers: { Accept: 'application/json' },
          credentials: 'same-origin',
          cache: 'no-store',
        });

        if (!response.ok) return;
        const cart = await response.json();
        if (requestId !== this.cartCountSyncRequest) return;

        this.applyCartCount(cart);
      } catch (error) {
        console.warn('Cardology header: unable to sync cart count.', error);
      }
    }

    onRootClick(event) {
      const megaTrigger = event.target.closest('[data-cdo-mega-trigger]');
      if (megaTrigger && this.root.contains(megaTrigger)) {
        event.preventDefault();
        const name = megaTrigger.dataset.cdoMegaTrigger;
        if (megaTrigger.getAttribute('aria-expanded') === 'true') {
          this.closeMega();
        } else {
          this.openMega(name, megaTrigger);
        }
        return;
      }

      const mobileOpen = event.target.closest('[data-cdo-mobile-open]');
      if (mobileOpen && this.root.contains(mobileOpen)) {
        event.preventDefault();
        this.openMobileDrawer(mobileOpen);
        return;
      }

      const mobileClose = event.target.closest('[data-cdo-mobile-close]');
      if (mobileClose && this.root.contains(mobileClose)) {
        event.preventDefault();
        event.stopPropagation();
        this.closeMobileDrawer();
        return;
      }

      const searchOpen = event.target.closest('[data-cdo-search-open]');
      if (searchOpen && this.root.contains(searchOpen)) {
        event.preventDefault();
        const cameFromDrawer = Boolean(searchOpen.closest('[data-cdo-mobile-drawer]'));
        if (cameFromDrawer) this.closeMobileDrawer(false);
        window.requestAnimationFrame(() => this.openSearch(searchOpen));
        return;
      }

      const searchClose = event.target.closest('[data-cdo-search-close]');
      if (searchClose && this.root.contains(searchClose)) {
        event.preventDefault();
        this.closeSearch();
      }
    }

    openMega(name, trigger) {
      if (!name || window.matchMedia('(max-width: 1199px)').matches) return;

      this.cancelMegaClose();
      this.closeMobileDrawer(false);
      this.closeSearch(false);

      this.megaTriggers.forEach((item) => {
        item.setAttribute('aria-expanded', item === trigger ? 'true' : 'false');
      });

      this.megaPanels.forEach((panel) => {
        const shouldOpen = panel.dataset.cdoMegaPanel === name;
        panel.hidden = !shouldOpen;
        panel.classList.toggle('is-open', shouldOpen);
        panel.setAttribute('aria-hidden', shouldOpen ? 'false' : 'true');
      });
    }

    scheduleMegaClose() {
      this.cancelMegaClose();
      this.closeTimer = window.setTimeout(() => this.closeMega(), 180);
    }

    cancelMegaClose() {
      if (this.closeTimer !== null) {
        window.clearTimeout(this.closeTimer);
        this.closeTimer = null;
      }
    }

    closeMega() {
      this.cancelMegaClose();
      this.megaTriggers.forEach((item) => item.setAttribute('aria-expanded', 'false'));
      this.megaPanels.forEach((panel) => {
        panel.classList.remove('is-open');
        panel.hidden = true;
        panel.setAttribute('aria-hidden', 'true');
      });
    }

    openMobileDrawer(trigger) {
      if (!this.mobileDrawer) return;

      this.closeMega();
      this.closeSearch(false);
      this.lastFocusedElement = trigger || document.activeElement;
      this.mobileDrawer.hidden = false;
      this.mobileDrawer.setAttribute('aria-hidden', 'false');
      window.requestAnimationFrame(() => {
        this.mobileDrawer.classList.add('is-open');
        document.body.classList.add(LOCK_CLASS);
        const closeButton = this.mobileDrawer.querySelector('[data-cdo-mobile-close]');
        if (closeButton) closeButton.focus({ preventScroll: true });
      });
    }

    closeMobileDrawer(restoreFocus = true) {
      if (!this.mobileDrawer) return;

      this.mobileDrawer.classList.remove('is-open');
      this.mobileDrawer.hidden = true;
      this.mobileDrawer.setAttribute('aria-hidden', 'true');
      this.releaseBodyLock();

      if (restoreFocus && this.lastFocusedElement instanceof HTMLElement) {
        this.lastFocusedElement.focus({ preventScroll: true });
      }
    }

    openSearch(trigger) {
      if (!this.searchOverlay) return;

      this.closeMega();
      this.closeMobileDrawer(false);
      this.lastFocusedElement = trigger || document.activeElement;
      this.searchOverlay.hidden = false;
      this.searchOverlay.setAttribute('aria-hidden', 'false');

      window.requestAnimationFrame(() => {
        this.searchOverlay.classList.add('is-open');
        document.body.classList.add(LOCK_CLASS);

        window.setTimeout(() => {
          const desktop = window.matchMedia('(min-width: 768px)').matches;
          const selector = desktop
            ? '[data-cdo-search-input-desktop]'
            : '[data-cdo-search-input-mobile]';
          const input = this.searchOverlay.querySelector(selector);
          if (input) input.focus({ preventScroll: true });
        }, 40);
      });
    }

    cleanupExternalSearchPopups() {
      document.querySelectorAll(
        'ul.ui-autocomplete.glFilter-search-popup, .ui-autocomplete.glFilter-search-popup, .glFilter-search-popup'
      ).forEach((popup) => {
        if (this.root.contains(popup)) return;
        popup.remove();
      });

      const drawerOpen = this.mobileDrawer && !this.mobileDrawer.hidden;
      const searchOpen = this.searchOverlay && !this.searchOverlay.hidden;
      if (!drawerOpen && !searchOpen) {
        document.body.classList.remove('gl-search-open');
      }
    }

    closeSearch(restoreFocus = true) {
      if (!this.searchOverlay) return;

      const activeSearchInput = this.searchOverlay.querySelector('[data-cdo-search-input]:focus');
      if (activeSearchInput instanceof HTMLElement) activeSearchInput.blur();

      this.cleanupExternalSearchPopups();
      this.searchOverlay.classList.remove('is-open');
      this.searchOverlay.hidden = true;
      this.searchOverlay.setAttribute('aria-hidden', 'true');
      this.releaseBodyLock();

      if (restoreFocus && this.lastFocusedElement instanceof HTMLElement) {
        this.lastFocusedElement.focus({ preventScroll: true });
      }

      window.requestAnimationFrame(() => this.cleanupExternalSearchPopups());
      window.setTimeout(() => this.cleanupExternalSearchPopups(), 120);
    }

    releaseBodyLock() {
      const drawerOpen = this.mobileDrawer && !this.mobileDrawer.hidden;
      const searchOpen = this.searchOverlay && !this.searchOverlay.hidden;
      if (!drawerOpen && !searchOpen) {
        document.body.classList.remove(LOCK_CLASS, 'gl-search-open');
      }
    }

    onDocumentPointerDown(event) {
      if (!this.root.contains(event.target)) this.closeMega();
    }

    onDocumentKeydown(event) {
      if (event.key === 'Escape') {
        const searchWasOpen = this.searchOverlay && !this.searchOverlay.hidden;
        const drawerWasOpen = this.mobileDrawer && !this.mobileDrawer.hidden;

        if (searchWasOpen) {
          this.closeSearch();
        } else if (drawerWasOpen) {
          this.closeMobileDrawer();
        } else {
          this.closeMega();
        }
        return;
      }

      if (event.key !== 'Tab') return;

      const activeModal = this.searchOverlay && !this.searchOverlay.hidden
        ? this.searchOverlay
        : this.mobileDrawer && !this.mobileDrawer.hidden
          ? this.mobileDrawer
          : null;

      if (!activeModal) return;

      const focusable = Array.from(activeModal.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), summary, [tabindex]:not([tabindex="-1"])'
      )).filter((element) => !element.hidden && element.offsetParent !== null);

      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus({ preventScroll: true });
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus({ preventScroll: true });
      }
    }

    onResize() {
      if (window.matchMedia('(min-width: 1200px)').matches) {
        this.closeMobileDrawer(false);
      } else {
        this.closeMega();
      }
    }
  }

  const initialise = (scope = document) => {
    const roots = [];
    if (scope.matches && scope.matches(ROOT_SELECTOR)) roots.push(scope);
    if (scope.querySelectorAll) roots.push(...scope.querySelectorAll(ROOT_SELECTOR));

    roots.forEach((root) => {
      if (controllers.has(root)) return;
      controllers.set(root, new HeaderController(root));
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialise(), { once: true });
  } else {
    initialise();
  }

  document.addEventListener('shopify:section:load', (event) => initialise(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    const root = event.target.matches && event.target.matches(ROOT_SELECTOR)
      ? event.target
      : event.target.querySelector && event.target.querySelector(ROOT_SELECTOR);

    if (!root || !controllers.has(root)) return;
    controllers.get(root).destroy();
    controllers.delete(root);
  });
})();